import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Icon } from './Icons.tsx';
import { dateLabel, shiftDate, todayMarket } from '../format.ts';

// A date field that opens a small calendar instead of the browser's own date box (which reads
// "10/22/2026 📅" and looks unfinished). One month, Monday first, six fixed rows so it never jumps;
// a bottom sheet on phones. Arrow keys move between days, Enter picks, Escape closes.

const DOW = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
const monthOf = (d: string) => d.slice(0, 7);
const addMonth = (ym: string, n: number) => {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + n, 1, 12));
  return d.toISOString().slice(0, 7);
};

/** The 42 days shown for a month: from the Monday on or before the 1st. */
function grid(ym: string) {
  const first = `${ym}-01`;
  const start = shiftDate(first, -((new Date(first + 'T12:00:00Z').getUTCDay() + 6) % 7));
  return Array.from({ length: 42 }, (_, i) => shiftDate(start, i));
}

export default function DateInput({ value, onChange, label, max, min, placeholder = 'Any date', clearable = false, selectable }: {
  value: string; onChange: (date: string) => void; label: string;
  max?: string; min?: string; placeholder?: string; clearable?: boolean;
  /** Grays out days that can't be picked, beyond `min` and `max`. */
  selectable?: (date: string) => boolean;
}) {
  const today = todayMarket();
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(monthOf(value || today));
  const box = useRef<HTMLDivElement>(null);
  const days = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    setMonth(monthOf(value || today));
    const outside = (e: PointerEvent) => { if (!box.current?.contains(e.target as Node)) setOpen(false); };
    const esc = (e: globalThis.KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', esc);
    // Start on the chosen day (or today) so the arrow keys work straight away.
    requestAnimationFrame(() => (days.current?.querySelector<HTMLButtonElement>('button.on') ?? days.current?.querySelector<HTMLButtonElement>('button.today'))?.focus());
    return () => { document.removeEventListener('pointerdown', outside); document.removeEventListener('keydown', esc); };
  }, [open]);

  const allowed = (d: string) => (!max || d <= max) && (!min || d >= min) && (!selectable || selectable(d));
  const pick = (d: string) => { if (allowed(d)) { onChange(d); setOpen(false); } };
  const move = (e: KeyboardEvent<HTMLButtonElement>, d: string) => {
    const step = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 }[e.key];
    if (!step) return;
    e.preventDefault();
    // Step over days that can't be picked (they can't take focus), but not forever.
    let next = shiftDate(d, step);
    for (let i = 0; i < 31 && !allowed(next); i++) next = shiftDate(next, step);
    if (!allowed(next)) return;
    if (monthOf(next) !== month) setMonth(monthOf(next));
    requestAnimationFrame(() => days.current?.querySelector<HTMLButtonElement>(`button[data-date="${next}"]`)?.focus());
  };

  return (
    <div className="date-input" ref={box}>
      <button type="button" className="date-trigger" onClick={() => setOpen(!open)} aria-haspopup="dialog" aria-expanded={open}
        aria-label={`${label}: ${value ? dateLabel(value) : placeholder}`}>
        <Icon.calendar />
        <span className={value ? '' : 'muted'}>{value ? dateLabel(value, { month: 'short', day: 'numeric', year: 'numeric' }) : placeholder}</span>
      </button>
      {open && (
        <div className="date-pop" role="dialog" aria-label={label}>
          <div className="date-head">
            <button type="button" className="small ghost" onClick={() => setMonth(addMonth(month, -1))} aria-label="Previous month"><Icon.chevronLeft /></button>
            <b>{dateLabel(`${month}-01`, { month: 'long', year: 'numeric' })}</b>
            <button type="button" className="small ghost" onClick={() => setMonth(addMonth(month, 1))} disabled={!!max && `${addMonth(month, 1)}-01` > max} aria-label="Next month"><Icon.chevronRight /></button>
          </div>
          <div className="date-grid" ref={days}>
            {DOW.map((d, i) => <span key={i} className="date-dow" aria-hidden="true">{d}</span>)}
            {grid(month).map((d) => (
              <button key={d} type="button" data-date={d} disabled={!allowed(d)} onClick={() => pick(d)} onKeyDown={(e) => move(e, d)}
                className={[d === value && 'on', d === today && 'today', monthOf(d) !== month && 'out'].filter(Boolean).join(' ')}
                aria-label={dateLabel(d)} aria-pressed={d === value}>
                {Number(d.slice(8))}
              </button>
            ))}
          </div>
          <div className="date-foot">
            {clearable && value && <button type="button" className="small ghost" onClick={() => { onChange(''); setOpen(false); }}>Clear</button>}
            <span className="spacer" />
            {allowed(today) && <button type="button" className="small" onClick={() => pick(today)}>Today</button>}
          </div>
        </div>
      )}
    </div>
  );
}
